import React, { useEffect, useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Badge,
  Spinner,
  Input,
  FormGroup,
  Label
} from 'reactstrap';
import QRCode from 'qrcode.react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { BaseCustomButton, BaseCustomModal, GradientDecorationLine } from '../../../../../shared';
import { getApiCall, postApiCall, patchApiCall } from '../../../../../services/mainServices';
import { getLabRequestSamples } from '../../../../../store/actions';

export const MassiveValidationModal = ({
  showModal,
  setShowModal,
  labRequestId,
  labRequest,
  labRequestTests = [],
  onSuccess
}) => {
  const dispatch = useDispatch();

  // Get samples from Redux store if already loaded
  const reduxSamples = useSelector(
    state => state.labRequestSampleReducer?.labRequestSamples || []
  );

  const [localSamples, setLocalSamples] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testConfigs, setTestConfigs] = useState({});
  const [globalEssays, setGlobalEssays] = useState('1');
  const [statusTest, setStatusTest] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Resolved list of samples (prioritize local state -> redux -> labRequest props)
  const samples = useMemo(() => {
    if (localSamples.length > 0) return localSamples;
    if (reduxSamples.length > 0) return reduxSamples;
    if (Array.isArray(labRequest?.labRequestSample) && labRequest.labRequestSample.length > 0) {
      return labRequest.labRequestSample;
    }
    return [];
  }, [localSamples, reduxSamples, labRequest]);

  // When modal opens, initialize test configs immediately and fetch samples if missing
  useEffect(() => {
    if (showModal && labRequestId) {
      // 1. Initialize status
      if (labRequestTests.length > 0) {
        const firstStatus = labRequestTests[0]?.statusTest || '';
        const allSame = labRequestTests.every(t => t.statusTest === firstStatus);
        setStatusTest(allSame ? firstStatus : '');
      }

      // 2. Initialize testConfigs immediately so modal renders with 0 delay
      const initialConfigs = {};
      labRequestTests.forEach(test => {
        const assignedSamples = test.labRequestTestSample || [];
        const preSelectedIds = assignedSamples.map(ts => ts.labRequestSample?.id).filter(Boolean);
        const essaysCount = assignedSamples.length > 0 ? (assignedSamples[0].essays || '1') : '1';

        initialConfigs[test.id] = {
          essays: String(essaysCount),
          selectedSampleIds: preSelectedIds,
          isOpen: true,
        };
      });
      setTestConfigs(initialConfigs);

      // 3. If samples not in memory, fetch them
      if (samples.length === 0) {
        setLoadingData(true);
        getApiCall('lab-request-samples', {
          limit: 100,
          page: 0,
          labRequest: labRequestId,
        })
          .then(res => {
            const list = Array.isArray(res?.data?.items)
              ? res.data.items
              : Array.isArray(res?.items)
              ? res.items
              : Array.isArray(res?.data)
              ? res.data
              : [];
            setLocalSamples(list);
          })
          .catch(err => {
            console.error('Error fetching samples for modal:', err);
          })
          .finally(() => {
            setLoadingData(false);
          });
      } else {
        setLoadingData(false);
      }
    }
  }, [showModal, labRequestId]);

  const closeModal = () => {
    setShowModal(false);
  };

  // Toggle accordion open/close for a single test
  const toggleAccordion = (testId) => {
    setTestConfigs(prev => ({
      ...prev,
      [testId]: {
        ...prev[testId],
        isOpen: !prev[testId]?.isOpen,
      },
    }));
  };

  // Expand / Collapse all accordions
  const toggleAllAccordions = (open) => {
    setTestConfigs(prev => {
      const updated = {};
      Object.keys(prev).forEach(testId => {
        updated[testId] = {
          ...prev[testId],
          isOpen: open,
        };
      });
      return updated;
    });
  };

  // Change essays per sample for a specific test
  const handleEssaysChange = (testId, value) => {
    setTestConfigs(prev => ({
      ...prev,
      [testId]: {
        ...prev[testId],
        essays: value,
      },
    }));
  };

  // Apply global essays number to all tests
  const applyGlobalEssays = () => {
    if (!globalEssays || Number(globalEssays) < 1) {
      toast.warning('Ingrese un número válido de ensayos por muestra (mínimo 1)');
      return;
    }
    setTestConfigs(prev => {
      const updated = {};
      Object.keys(prev).forEach(testId => {
        updated[testId] = {
          ...prev[testId],
          essays: globalEssays,
        };
      });
      return updated;
    });
    toast.info(`Se aplicaron ${globalEssays} ensayos por muestra a todos los ensayos`);
  };

  // Toggle single sample for a specific test
  const toggleSampleForTest = (testId, sampleId) => {
    setTestConfigs(prev => {
      const currentSelected = prev[testId]?.selectedSampleIds || [];
      const exists = currentSelected.includes(sampleId);
      const newSelected = exists
        ? currentSelected.filter(id => id !== sampleId)
        : [...currentSelected, sampleId];

      return {
        ...prev,
        [testId]: {
          ...prev[testId],
          selectedSampleIds: newSelected,
        },
      };
    });
  };

  // Select / Deselect all samples for a specific test
  const selectAllSamplesForTest = (testId, selectAll) => {
    setTestConfigs(prev => ({
      ...prev,
      [testId]: {
        ...prev[testId],
        selectedSampleIds: selectAll ? samples.map(s => s.id) : [],
      },
    }));
  };

  // Global: Select all samples for all tests
  const selectAllSamplesGlobally = () => {
    const allSampleIds = samples.map(s => s.id);
    setTestConfigs(prev => {
      const updated = {};
      Object.keys(prev).forEach(testId => {
        updated[testId] = {
          ...prev[testId],
          selectedSampleIds: [...allSampleIds],
        };
      });
      return updated;
    });
    toast.info('Se seleccionaron todas las muestras en todos los ensayos');
  };

  // Global: Deselect all samples for all tests
  const deselectAllSamplesGlobally = () => {
    setTestConfigs(prev => {
      const updated = {};
      Object.keys(prev).forEach(testId => {
        updated[testId] = {
          ...prev[testId],
          selectedSampleIds: [],
        };
      });
      return updated;
    });
  };

  // Filter samples by search term
  const filteredSamples = useMemo(() => {
    if (!filterSearch.trim()) return samples;
    const term = filterSearch.toLowerCase().trim();
    return samples.filter(s =>
      (s.name || '').toLowerCase().includes(term) ||
      (s.serviceIdentifier || '').toLowerCase().includes(term) ||
      (s.sampleControlIdentifier || '').toLowerCase().includes(term)
    );
  }, [samples, filterSearch]);

  // Handle Save All
  const handleSaveAll = async () => {
    // 1. Identify which tests have samples configured
    const testsToSave = [];
    for (const test of labRequestTests) {
      const config = testConfigs[test.id];
      const selected = config?.selectedSampleIds || [];
      if (selected.length > 0) {
        const essaysCount = Number(config?.essays);
        if (!essaysCount || essaysCount < 1) {
          toast.error(`El ensayo "${test.labTest?.name || 'Sin nombre'}" debe tener al menos 1 ensayo por muestra.`);
          return;
        }
        testsToSave.push({
          labRequestTest: test.id,
          labRequestSamples: selected,
          essays: essaysCount,
          testName: test.labTest?.name,
        });
      }
    }

    if (!statusTest && testsToSave.length === 0) {
      toast.warning('Seleccione un estatus o configure muestras para asignar a los ensayos.');
      return;
    }

    try {
      setSaving(true);

      // 1. Update Status massively if selected
      if (statusTest) {
        await patchApiCall(
          `lab-request-tests/massive/status-update?labRequest=${labRequestId}&statusTest=${statusTest}`,
          {}
        );
      }

      // 2. Save sample assignments sequentially for all configured tests
      if (testsToSave.length > 0) {
        for (const item of testsToSave) {
          await postApiCall('lab-request-test-samples', {
            labRequestTest: item.labRequestTest,
            labRequestSamples: item.labRequestSamples,
            essays: item.essays,
          });
        }
      }

      toast.success('Validación masiva y asignación de muestras guardadas correctamente.');
      if (onSuccess) onSuccess();
      closeModal();
    } catch (error) {
      console.error('Error saving massive essay validation & assignments:', error);
      toast.error('Ocurrió un error al guardar los cambios de validación masiva.');
    } finally {
      setSaving(false);
    }
  };

  // Total summary calculation
  const totalAssignedSamples = useMemo(() => {
    let count = 0;
    Object.values(testConfigs).forEach(cfg => {
      count += (cfg?.selectedSampleIds || []).length;
    });
    return count;
  }, [testConfigs]);

  return (
    <BaseCustomModal
      title={
        <div className="d-flex align-items-center justify-content-between pe-4">
          <div>
            <h4 className="text-primary m-0 fw-bold">
              <i className="mdi mdi-checkbox-marked-circle-outline me-2 text-info"></i>
              Validación y Asignación Masiva de Ensayos
            </h4>
            <small className="text-muted">
              Actualice el estatus y asigne las muestras a todos los ensayos en un solo paso
            </small>
          </div>
          {labRequest?.folio && (
            <Badge color="info" className="px-3 py-2 fs-6">
              Folio: {labRequest.folio}
            </Badge>
          )}
        </div>
      }
      isModalOpen={showModal}
      handleOk={handleSaveAll}
      handleCancel={closeModal}
      width="85%"
      centered
      style={{ top: 20, maxHeight: '90vh' }}
      footer={null}
    >
      <GradientDecorationLine />

      {loadingData && samples.length === 0 ? (
        <div className="text-center py-5">
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Cargando muestras de la solicitud...</p>
        </div>
      ) : (
        <div style={{ maxHeight: 'calc(80vh - 170px)', overflowY: 'auto', paddingRight: '6px' }}>
          {/* Top Control Bar: Estatus + Quick-Actions */}
          <Card className="border shadow-none mb-3 bg-light">
            <CardBody className="py-2 px-3">
              <Row className="align-items-center g-2">
                {/* 1. Status Selection */}
                <Col md={3} sm={12}>
                  <div className="d-flex align-items-center">
                    <Label className="me-2 mb-0 text-nowrap fw-bold text-primary" style={{ fontSize: '0.88rem' }}>
                      <i className="mdi mdi-format-list-checks me-1"></i>Estatus:
                    </Label>
                    <Input
                      type="select"
                      className="form-select form-select-sm fw-bold border-primary text-primary"
                      style={{ maxWidth: '140px' }}
                      value={statusTest}
                      onChange={(e) => setStatusTest(e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      <option value="Pendiente">Pendiente</option>
                      <option value="Rechazado">Rechazado</option>
                      <option value="Autorizado">Autorizado</option>
                    </Input>
                  </div>
                </Col>

                {/* 2. Global Essays Input */}
                <Col md={4} sm={12}>
                  <div className="d-flex align-items-center">
                    <Label className="me-2 mb-0 text-nowrap fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                      Ensayos por muestra:
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      className="form-control form-control-sm text-center fw-bold me-2"
                      style={{ width: '65px' }}
                      value={globalEssays}
                      onChange={(e) => setGlobalEssays(e.target.value)}
                    />
                    <BaseCustomButton
                      color="secondary"
                      label="Aplicar a todos"
                      className="btn-sm py-1 px-2 rounded-3 text-nowrap"
                      icon="mdi mdi-arrow-down-bold-box me-1"
                      onClick={applyGlobalEssays}
                    />
                  </div>
                </Col>

                {/* 3. Global Sample Selection Buttons */}
                <Col md={3} sm={12}>
                  <div className="d-flex gap-1 justify-content-center">
                    <BaseCustomButton
                      color="info"
                      label="Seleccionar todas"
                      className="btn-sm py-1 px-2 rounded-3 text-nowrap"
                      icon="mdi mdi-checkbox-multiple-marked me-1"
                      onClick={selectAllSamplesGlobally}
                    />
                    <BaseCustomButton
                      color="light"
                      label="Limpiar"
                      className="btn-sm py-1 px-2 rounded-3 text-nowrap border"
                      icon="mdi mdi-checkbox-multiple-blank-outline me-1"
                      onClick={deselectAllSamplesGlobally}
                    />
                  </div>
                </Col>

                {/* 4. Expand / Collapse All */}
                <Col md={2} sm={12} className="text-end">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none text-muted p-0"
                      onClick={() => toggleAllAccordions(true)}
                    >
                      <i className="mdi mdi-chevron-double-down me-1"></i>Expandir
                    </button>
                    <span className="text-muted">|</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none text-muted p-0"
                      onClick={() => toggleAllAccordions(false)}
                    >
                      <i className="mdi mdi-chevron-double-up me-1"></i>Colapsar
                    </button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Search sample filter */}
          {samples.length > 4 && (
            <div className="mb-3 px-1">
              <Input
                type="search"
                className="form-control form-control-sm rounded-3"
                placeholder="🔍 Filtrar muestras por nombre o código..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </div>
          )}

          {/* Essay Accordion Cards List */}
          {labRequestTests.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="mdi mdi-alert-circle-outline fs-1 text-warning"></i>
              <p className="mt-2">No se encontraron ensayos registrados en esta solicitud.</p>
            </div>
          ) : (
            labRequestTests.map((test, index) => {
              const config = testConfigs[test.id] || { essays: '1', selectedSampleIds: [], isOpen: true };
              const selectedCount = (config.selectedSampleIds || []).length;
              const allSelected = samples.length > 0 && selectedCount === samples.length;

              return (
                <Card key={test.id} className="mb-3 border rounded-3 shadow-sm">
                  <CardHeader
                    className="py-2 px-3 bg-white d-flex align-items-center justify-content-between"
                    style={{ cursor: 'pointer', borderBottom: config.isOpen ? '1px solid #e9ebec' : 'none' }}
                    onClick={() => toggleAccordion(test.id)}
                  >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <span className="badge bg-primary rounded-pill px-2 py-1" style={{ fontSize: '0.8rem' }}>
                        #{index + 1}
                      </span>
                      <div>
                        <strong className="text-dark me-2" style={{ fontSize: '0.95rem' }}>
                          {test.labTest?.name ?? 'Ensayo sin nombre'}
                        </strong>
                        <div className="text-muted small mt-1">
                          <span className="me-3">
                            <strong>Norma:</strong> {test.labTest?.norm?.name ?? '-'}
                          </span>
                          <span>
                            <strong>Método:</strong> {test.labTest?.method ?? '-'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {test.statusTest && (
                        <Badge
                          color={
                            test.statusTest === 'Autorizado'
                              ? 'success'
                              : test.statusTest === 'Rechazado'
                              ? 'danger'
                              : 'warning'
                          }
                          className="px-2 py-1 small"
                        >
                          {test.statusTest}
                        </Badge>
                      )}
                      <Badge
                        color={selectedCount > 0 ? 'success' : 'secondary'}
                        className="px-2 py-1 fs-7"
                      >
                        {selectedCount} de {samples.length} muestra(s)
                      </Badge>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-dark p-0 ms-2"
                        onClick={() => toggleAccordion(test.id)}
                      >
                        <i
                          className={`mdi ${
                            config.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
                          } fs-4 text-primary`}
                        ></i>
                      </button>
                    </div>
                  </CardHeader>

                  <Collapse isOpen={config.isOpen}>
                    <CardBody className="p-3 bg-light bg-opacity-50">
                      {/* Configuration Controls per Essay */}
                      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                          <Label className="mb-0 me-2 fw-semibold text-primary" style={{ fontSize: '0.88rem' }}>
                            <i className="mdi mdi-numeric me-1"></i>Ensayos por muestra:
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            className="form-control form-control-sm text-center fw-bold text-primary"
                            style={{ width: '80px' }}
                            value={config.essays}
                            onChange={(e) => handleEssaysChange(test.id, e.target.value)}
                          />
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary py-1 px-2 rounded-2"
                            onClick={() => selectAllSamplesForTest(test.id, !allSelected)}
                          >
                            <i className={`mdi ${allSelected ? 'mdi-checkbox-blank-outline' : 'mdi-checkbox-marked-outline'} me-1`}></i>
                            {allSelected ? 'Deseleccionar todas' : 'Seleccionar todas las muestras'}
                          </button>
                        </div>
                      </div>

                      {/* Samples Grid List */}
                      {filteredSamples.length === 0 ? (
                        <div className="text-center py-3 text-muted">
                          <small>No hay muestras disponibles para asignar</small>
                        </div>
                      ) : (
                        <Row className="g-2">
                          {filteredSamples.map((sample) => {
                            const isChecked = (config.selectedSampleIds || []).includes(sample.id);

                            return (
                              <Col md={6} lg={4} sm={12} key={sample.id}>
                                <div
                                  className={`p-2 rounded-3 border d-flex align-items-center justify-content-between transition-all ${
                                    isChecked
                                      ? 'bg-success bg-opacity-10 border-success shadow-sm'
                                      : 'bg-white border-light'
                                  }`}
                                  style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease-in-out',
                                  }}
                                  onClick={() => toggleSampleForTest(test.id, sample.id)}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <FormGroup check className="mb-0 p-0 ms-1">
                                      <Input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {}} // Controlled by card onClick
                                        className="form-check-input rounded-circle p-2"
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </FormGroup>
                                    <div className="d-none d-sm-block">
                                      <QRCode
                                        value={sample.sampleControlIdentifier || sample.serviceIdentifier || 'N/A'}
                                        size={36}
                                      />
                                    </div>
                                    <div className="ms-1" style={{ lineHeight: '1.2' }}>
                                      <div className="fw-bold text-dark text-truncate" style={{ maxWidth: '180px', fontSize: '0.85rem' }}>
                                        {sample.name ?? 'Sin descripción'}
                                      </div>
                                      <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                        Folio: <strong>{sample.serviceIdentifier ?? '-'}</strong> | Código: {sample.sampleControlIdentifier ?? '-'}
                                      </small>
                                      <small className="text-secondary" style={{ fontSize: '0.72rem' }}>
                                        Cant: {sample.quantity ?? '-'} {sample.unitMeasure?.name ?? ''}
                                      </small>
                                    </div>
                                  </div>

                                  {isChecked && (
                                    <i className="mdi mdi-check-circle text-success fs-5 me-1"></i>
                                  )}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      )}
                    </CardBody>
                  </Collapse>
                </Card>
              );
            })
          )}
        </div>
      )}

      <GradientDecorationLine />

      {/* Footer Controls */}
      <div className="d-flex align-items-center justify-content-between pt-2">
        <div className="text-muted small">
          <i className="mdi mdi-information-outline me-1 text-info"></i>
          {statusTest ? (
            <>Estatus a aplicar: <strong>{statusTest}</strong> | </>
          ) : null}
          Total asignaciones configuradas: <strong>{totalAssignedSamples}</strong> en <strong>{labRequestTests.length}</strong> ensayo(s)
        </div>

        <div className="d-flex gap-2">
          <BaseCustomButton
            color="success"
            label={saving ? 'Guardando...' : 'Guardar Todo'}
            onClick={handleSaveAll}
            disabled={saving || loadingData || labRequestTests.length === 0}
            className="rounded-3 px-4 py-2"
            icon={saving ? 'mdi mdi-loading mdi-spin me-1' : 'mdi mdi-content-save-all me-1'}
          />
          <BaseCustomButton
            color="danger"
            label="Cancelar"
            onClick={closeModal}
            disabled={saving}
            outline
            className="rounded-3 px-3 py-2"
            icon="mdi mdi-close-circle me-1"
          />
        </div>
      </div>
    </BaseCustomModal>
  );
};
